import { IQService, IHttpInterceptor } from "angular";

const cacheKey = "apexCompatibleHttpMethods";

/**
 * An interceptor that tests for ability to make PUT/PATCH/DELETE requests
 * and transforms requests into POST requests with the X-HTTP-Method-Override
 * header set if the connection is blocked.
 * @param $q An instance of a service with deferred capabilities.
 * @param apexApiUrl The root URL to the Apex API
 * @returns An interceptor that transforms unsupported methods
 * into POST methods and adds an X-HTTP-Method-Override header.
 */
export function XHttpMethodOverrideInterceptor($q: IQService, apexApiUrl: string): IHttpInterceptor {
    'ngInject'
    const svc: any = {};
    const methods = ["PUT", "PATCH", "DELETE"];

    activate();

    return {
        request: function (config) {
            const method = config.method.toUpperCase();
            if (methods.find(p => p === method) && svc.canUseMethod && !svc.canUseMethod[method]) {
                // Override the method with POST.
                config.headers["X-HTTP-Method-Override"] = method;
                config.method = "POST";
            }
            return config;
        }
    };

    function activate() {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            svc.canUseMethod = JSON.parse(cached);
            return $q.resolve(svc.canUseMethod);
        }

        // This is instantiated during $http's configuration, so $http itself won't be available.
        // Use XHR instead.
        $q.all(methods.map(function (method) {
            return $q((resolve, reject) => {
                const request = new XMLHttpRequest();

                request.addEventListener("load", function () {
                    // eslint-disable-next-line no-invalid-this,@typescript-eslint/no-this-alias
                    const request = this;

                    // Always resolve, but resolve to success: false if the request failed.
                    resolve({
                        method: method,
                        success: request.status >= 200 && request.status < 300
                    });
                });
                request.open(method, apexApiUrl + "services/shim-tests/http-" + method.toLowerCase(), true);
                request.send();
            });
        })).then(function (v: { method: string, success: boolean }[]) {
            svc.canUseMethod = v.reduce(function (methods, test) {
                methods[test.method] = test.success;
                return methods;
            }, { GET: true, POST: true });
            localStorage.setItem(cacheKey, JSON.stringify(svc.canUseMethod));
        });
    }
}
